import { ChannelValue, CompanyValue, UserValue, Value } from 'components/ShareSheet/RecipientSelector/Types'
import { RootStore } from 'stores/RootStore'
import { AccessMemberGroup, MinimalUserSerializerUser } from '../../types'
import { CreateReviewActionConfig } from 'stores/Workflows/Types'
import { RecipientsSelectorValueType } from 'types'

export type UserReviewer = { type: RecipientsSelectorValueType.User; value: number }
export type ChannelReviewer = { type: RecipientsSelectorValueType.Channel; value: number }
export type EmailReviewer = { type: RecipientsSelectorValueType.Email; value: string }
export type AccessMemberGroupReviewer = {
  type: RecipientsSelectorValueType.AccessMemberGroup
  value: AccessMemberGroup
}

export type Reviewer = UserReviewer | ChannelReviewer | EmailReviewer | AccessMemberGroupReviewer

export const parseReviewers = (users: number[], channels: number[]): Array<UserReviewer | ChannelReviewer> => {
  const parsedUsers: UserReviewer[] = users.map((userId) => ({
    type: RecipientsSelectorValueType.User,
    value: userId,
  }))
  const parsedChannels: ChannelReviewer[] = channels.map((channelId) => ({
    type: RecipientsSelectorValueType.Channel,
    value: channelId,
  }))
  return [...parsedUsers, ...parsedChannels]
}

export const dehydrateValues = (values: Value[], rootStore: RootStore) =>
  values.map((recipientValue) => {
    if (recipientValue.type === RecipientsSelectorValueType.Channel) {
      rootStore.channels.add(recipientValue.value)
    }

    if (recipientValue.type === RecipientsSelectorValueType.User) {
      rootStore.users.add(recipientValue.value as unknown as MinimalUserSerializerUser)
    }
    const value = [RecipientsSelectorValueType.AccessMemberGroup, RecipientsSelectorValueType.Email].includes(
      recipientValue.type,
    )
      ? recipientValue.value
      : (recipientValue as UserValue | ChannelValue | CompanyValue).value.id

    return {
      type: recipientValue.type,
      value,
    } as Reviewer
  })

export const hydrateValues = (values: Reviewer[], rootStore: RootStore) =>
  values
    .map((reviewer) => {
      let hydratedValue: unknown = reviewer.value
      if (reviewer.type === RecipientsSelectorValueType.Channel) {
        hydratedValue = rootStore.channels.get(reviewer.value as number)
      }

      if (reviewer.type === RecipientsSelectorValueType.User) {
        hydratedValue = rootStore.users.get(reviewer.value as number)
      }

      if (!hydratedValue) {
        return false
      }

      return {
        type: reviewer.type,
        value: hydratedValue,
      } as Value
    })
    .filter(Boolean) as Value[]

export type RequestParticipantsBuckets = Pick<
  CreateReviewActionConfig,
  'reviewers' | 'channel_reviewers' | 'approvers' | 'channel_approvers' | 'viewers' | 'channel_viewers'
> & {
  email_viewers: string[]
  email_reviewers: string[]
  email_approvers: string[]
}

export const getParticipantsBuckets = (input: {
  reviewers: Reviewer[]
  approvers: Reviewer[]
  viewers: Reviewer[]
}): RequestParticipantsBuckets => {
  const reviewers: number[] = []
  const channel_reviewers: number[] = []
  const approvers: number[] = []
  const channel_approvers: number[] = []
  const viewers: number[] = []
  const channel_viewers: number[] = []
  const email_approvers: string[] = []
  const email_reviewers: string[] = []
  const email_viewers: string[] = []

  const mappings: Array<[Reviewer[], number[], number[], string[]]> = [
    [input.reviewers, reviewers, channel_reviewers, email_reviewers],
    [input.approvers, approvers, channel_approvers, email_approvers],
    [input.viewers, viewers, channel_viewers, email_viewers],
  ]
  mappings.forEach((mapping) => {
    const [origin, userBucket, channelBucket, emailBucket] = mapping
    origin.forEach((reviewer) => {
      if (reviewer.type === RecipientsSelectorValueType.User) {
        userBucket.push(reviewer.value)
      }
      if (reviewer.type === RecipientsSelectorValueType.Channel) {
        channelBucket.push(reviewer.value)
      }
      if (reviewer.type === RecipientsSelectorValueType.AccessMemberGroup) {
        reviewer.value.members.forEach((member) => userBucket.push(member.id))
        reviewer.value.channels.forEach((channel) => channelBucket.push(channel.id))
      }
      if (reviewer.type === RecipientsSelectorValueType.Email) {
        emailBucket.push(reviewer.value)
      }
    })
  })

  return {
    reviewers,
    channel_reviewers,
    approvers,
    channel_approvers,
    viewers,
    channel_viewers,
    email_approvers,
    email_reviewers,
    email_viewers,
  }
}
